import { useFormatDate, useFormatDateTime } from "@dgs/core";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataFieldIndexResource } from "../../../../../../shared/types/dataField";
import { Address } from "../../../../../../shared/ui/Address";
import { ContactAddressList } from "../../../../contacts/ContactAddressList";
import { useDataFieldsQuery } from "../../../../shared/api/dataFieldService";
import { ContactAttribute, ContactAttributeValue, ContactRelationAttribute } from "../../../../shared/types";
import {
	ContactUpdateAddressDiffEntryResource,
	ContactUpdateAddressDiffRowResource,
	ContactUpdateAddressSaveRequest,
	ContactUpdateCompanyDiffEntryResource,
	ContactUpdateCompanyDiffRowResource,
	ContactUpdateEmailDiffEntryResource,
	ContactUpdateEmailDiffRowResource,
	ContactUpdateInquiryAttributeDiffRowResource,
	ContactUpdateInquiryContactDiffResource,
	ContactUpdatePhoneDiffEntryResource,
	ContactUpdatePhoneDiffRowResource,
	ContactUpdateSaveRequest,
	ContactUpdateSaveStatus,
} from "../../../../shared/types/contactUpdateInquiry";
import { ContactUpdateFormValue, ContactUpdateFormValues } from "./contactUpdateTypes";

const emptyValue = "-";

export const resolveRelationAttributeValue = (relationAttribute?: ContactRelationAttribute) => {
	if (!relationAttribute) {
		return emptyValue;
	}
	let entry;
	if (relationAttribute.value) {
		switch (relationAttribute.entityType) {
			case "companies":
			case "nonPrimaryCompanies":
			case "tenants":
			case "tags":
			case "invitationLists":
			case "distributionLists":
				entry = relationAttribute.value.map((value) => value.name).join(", ");
				break;
			case "emails":
			case "nonPrimaryEmails":
				entry = relationAttribute.value.map((email) => email.email);
				break;
			case "phones":
			case "nonPrimaryPhones":
				entry = relationAttribute.value.map((phone) => phone.phoneNumber);
				break;
			case "addresses":
			case "nonPrimaryAddresses":
				entry = <ContactAddressList addresses={relationAttribute.value} />;
				break;
			case "primaryAddress":
				entry = <Address address={relationAttribute.value} />;
				break;
			case "primaryPhone":
				entry = relationAttribute.value.phoneNumber;
				break;
			case "primaryEmail":
				entry = relationAttribute.value.email;
				break;
			case "primaryCompany":
				entry = relationAttribute.value.name;
				break;
		}
	}
	return entry || emptyValue;
};

export const useContactAttributeUtils = () => {
	const { t } = useTranslation();
	const { data } = useDataFieldsQuery();
	const formatDateTime = useFormatDateTime();
	const formatDate = useFormatDate();

	const resolveDataFieldValue = useCallback(
		(value: any, dataField: DataFieldIndexResource) => {
			switch (dataField?.type) {
				case "checkbox":
					return value ? t("Yes") : t("No");
				case "selectField":
				case "radioField":
					return dataField.options.find((option) => `${option.id}` === `${value}`)?.value;
				case "dateTime":
					return formatDateTime(value);
				case "date":
					return formatDate(value);
				case "checkboxGroup":
				case "multiSelectField": {
					const options =
						dataField.options && value ? dataField.options.filter((option) => value.includes(option.id)) : [];
					return options.map((option) => option.value).join(", ");
				}
				case "phone":
					return value;
				case "email":
					return value;
				default:
					return value;
			}
		},
		[formatDate, formatDateTime, t],
	);

	const resolveColumnValue = useCallback(
		(attribute: ContactAttribute) => {
			switch (attribute.entityType) {
				case "createdAt":
				case "updatedAt":
					return formatDateTime(attribute.value);
				default:
					return attribute.value ? attribute.value : emptyValue;
			}
		},
		[formatDateTime],
	);

	const resolveAttributeValue = useCallback(
		(attribute: ContactAttribute) => {
			switch (attribute.type) {
				case "column":
					return resolveColumnValue(attribute);
				case "dataField": {
					const dataField = data.find((d) => attribute.entityType === d.id);
					return dataField ? resolveDataFieldValue(attribute.value, dataField) : emptyValue;
				}
				case "relation":
					return resolveRelationAttributeValue(attribute);
			}
		},
		[data, resolveColumnValue, resolveDataFieldValue],
	);

	return {
		resolveAttributeValue,
	};
};

export function addressDiffEntryToIdentifier(address: ContactUpdateAddressDiffEntryResource): string {
	return `${address.street}-${address.houseNumber}-${address.zip}-${address.city}`;
}

export function companyDiffEntryToIdentifier(company: ContactUpdateCompanyDiffEntryResource): string {
	return `${company.id}-${company.position}`;
}

export function phoneDiffEntryToIdentifier(entry: ContactUpdatePhoneDiffEntryResource): string {
	return entry.phoneNumber;
}

export function emailDiffEntryToIdentifier(entry: ContactUpdateEmailDiffEntryResource): string {
	return entry.email;
}

export function attributeDiffEntryToIdentifier(attribute: ContactAttribute): number {
	return attribute.id;
}

function mapAddressDiffEntryToAddressRequest(
	entry: ContactUpdateAddressDiffEntryResource,
): ContactUpdateAddressSaveRequest {
	return {
		city: entry.city,
		type: entry.type,
		additionalInfo: entry.additionalInfo,
		zip: entry.zip,
		houseNumber: entry.houseNumber,
		street: entry.street,
		countryId: entry.country.id,
		isPrimary: entry.isPrimary,
	};
}

function mapContactAttributeToValue(attribute: ContactAttribute): ContactAttributeValue {
	return {
		id: attribute.id,
		value: attribute.value,
	};
}

function emailsToRemove(formValues: ContactUpdateFormValues) {
	return (row: ContactUpdateEmailDiffRowResource): boolean => {
		if (formValues.emails[emailDiffEntryToIdentifier(row.currentContact)] === "updatedContact") {
			return !row.currentContact.shouldBeRemoved;
		}
		if (formValues.emails[emailDiffEntryToIdentifier(row.currentContact)] === "currentContact") {
			return !row.updatedContact.isNew;
		}

		return true;
	};
}

function toCorrespondingEmailRequestValue(formValues: ContactUpdateFormValues) {
	return (emailRow: ContactUpdateEmailDiffRowResource) => {
		if (formValues.emails[emailDiffEntryToIdentifier(emailRow.currentContact)] === "currentContact") {
			return emailRow.currentContact;
		}

		return emailRow.updatedContact;
	};
}

function toCorrespondingCompanyRequestValue(formValues: ContactUpdateFormValues) {
	return (companyRow: ContactUpdateCompanyDiffRowResource) => {
		if (formValues.companies[companyDiffEntryToIdentifier(companyRow.currentContact)] === "currentContact") {
			return companyRow.currentContact;
		}

		return companyRow.updatedContact;
	};
}

function phonesToRemove(formValues: ContactUpdateFormValues) {
	return (row: ContactUpdatePhoneDiffRowResource): boolean => {
		if (formValues.phones[phoneDiffEntryToIdentifier(row.currentContact)] === "updatedContact") {
			return !row.currentContact.shouldBeRemoved;
		}
		if (formValues.phones[phoneDiffEntryToIdentifier(row.currentContact)] === "currentContact") {
			return !row.updatedContact.isNew;
		}

		return true;
	};
}

function toCorrespondingPhoneRequestValue(formValues: ContactUpdateFormValues) {
	return (phoneRow: ContactUpdatePhoneDiffRowResource) => {
		if (formValues.phones[phoneDiffEntryToIdentifier(phoneRow.currentContact)] === "currentContact") {
			return phoneRow.currentContact;
		}

		return phoneRow.updatedContact;
	};
}

function addressToRemove(formValues: ContactUpdateFormValues) {
	return (row: ContactUpdateAddressDiffRowResource): boolean => {
		if (formValues.addresses[addressDiffEntryToIdentifier(row.currentContact)] === "updatedContact") {
			return !row.currentContact.shouldBeRemoved;
		}
		if (formValues.addresses[addressDiffEntryToIdentifier(row.currentContact)] === "currentContact") {
			return !row.updatedContact.isNew;
		}

		return true;
	};
}

function toCorrespondingAddressRequestValue(formValues: ContactUpdateFormValues) {
	return (addressRow: ContactUpdateAddressDiffRowResource) => {
		if (formValues.addresses[addressDiffEntryToIdentifier(addressRow.currentContact)] === "currentContact") {
			return mapAddressDiffEntryToAddressRequest(addressRow.currentContact);
		}

		return mapAddressDiffEntryToAddressRequest(addressRow.updatedContact);
	};
}

function toCorrespondingAttributeRequestValue(formValues: ContactUpdateFormValues) {
	return (attributeRow: ContactUpdateInquiryAttributeDiffRowResource) => {
		if (formValues.attributes[attributeDiffEntryToIdentifier(attributeRow.currentContact)] === "currentContact") {
			return mapContactAttributeToValue(attributeRow.currentContact);
		}

		return mapContactAttributeToValue(attributeRow.updatedContact);
	};
}

function isNotSamePhone(rows: ContactUpdatePhoneDiffRowResource[]) {
	return (identifier: string) => {
		return rows.some((row) => phoneDiffEntryToIdentifier(row.currentContact) === identifier && !row.isSame);
	};
}

function isNotSameEmail(rows: ContactUpdateEmailDiffRowResource[]) {
	return (identifier: string) => {
		return rows.some((row) => emailDiffEntryToIdentifier(row.currentContact) === identifier && !row.isSame);
	};
}

function isNotSameAddress(rows: ContactUpdateAddressDiffRowResource[]) {
	return (identifier: string) => {
		return rows.some((row) => addressDiffEntryToIdentifier(row.currentContact) === identifier && !row.isSame);
	};
}

function isNotSameCompany(rows: ContactUpdateCompanyDiffRowResource[]) {
	return (identifier: string) => {
		return rows.some((row) => companyDiffEntryToIdentifier(row.currentContact) === identifier && !row.isSame);
	};
}

function isNotSameAttribute(rows: ContactUpdateInquiryAttributeDiffRowResource[]) {
	return (identifier: string) => {
		return rows.some(
			(row) => attributeDiffEntryToIdentifier(row.currentContact) === parseInt(identifier) && !row.isSame,
		);
	};
}

export function mapContactUpdateFormValuesToRequest(
	formValues: ContactUpdateFormValues,
	contactUpdateDiff: ContactUpdateInquiryContactDiffResource,
): ContactUpdateSaveRequest {
	const phonesDiff = Object.keys(formValues.phones)
		.filter(isNotSamePhone(contactUpdateDiff.phones))
		.map((keys) => formValues.phones[keys]);
	const emailsDiff = Object.keys(formValues.emails)
		.filter(isNotSameEmail(contactUpdateDiff.emails))
		.map((keys) => formValues.emails[keys]);
	const addressesDiff = Object.keys(formValues.addresses)
		.filter(isNotSameAddress(contactUpdateDiff.addresses))
		.map((keys) => formValues.addresses[keys]);
	const companiesDiff = Object.keys(formValues.companies)
		.filter(isNotSameCompany(contactUpdateDiff.companies))
		.map((keys) => formValues.companies[keys]);
	const attributesDiff = Object.keys(formValues.attributes)
		.filter(isNotSameAttribute(contactUpdateDiff.attributes))
		.map((keys) => formValues.attributes[keys]);
	const changes = [...phonesDiff, ...emailsDiff, ...addressesDiff, ...companiesDiff, ...attributesDiff];

	let newStatus: ContactUpdateSaveStatus = "CHANGE_PARTIALLY_ACCEPTED";
	if (changes.every((value) => value === "updatedContact")) {
		newStatus = "CHANGE_ACCEPTED";
	} else if (changes.every((value) => value === "currentContact")) {
		newStatus = "CHANGE_REJECTED";
	}
	return {
		addresses: contactUpdateDiff.addresses
			.filter(addressToRemove(formValues))
			.map(toCorrespondingAddressRequestValue(formValues)),
		phones: contactUpdateDiff.phones
			.filter(phonesToRemove(formValues))
			.map(toCorrespondingPhoneRequestValue(formValues)),
		emails: contactUpdateDiff.emails
			.filter(emailsToRemove(formValues))
			.map(toCorrespondingEmailRequestValue(formValues)),
		companies: contactUpdateDiff.companies.map(toCorrespondingCompanyRequestValue(formValues)),
		attributes: contactUpdateDiff.attributes.map(toCorrespondingAttributeRequestValue(formValues)),
		newStatus: newStatus,
	};
}

export function mapContactUpdateInquiryContactDiffResourceToFormValues(
	contactUpdateDiff: ContactUpdateInquiryContactDiffResource,
): ContactUpdateFormValues {
	return {
		attributes: contactUpdateDiff.attributes.reduce(
			(values, entry) => ({ ...values, [`${entry.updatedContact.id}`]: "updatedContact" }),
			{} as Record<string, ContactUpdateFormValue>,
		),
		emails: contactUpdateDiff.emails.reduce(
			(values, entry) => ({ ...values, [`${entry.updatedContact.email}`]: "updatedContact" }),
			{} as Record<string, ContactUpdateFormValue>,
		),
		phones: contactUpdateDiff.phones.reduce(
			(values, entry) => ({ ...values, [`${entry.updatedContact.phoneNumber}`]: "updatedContact" }),
			{} as Record<string, ContactUpdateFormValue>,
		),
		addresses: contactUpdateDiff.addresses.reduce(
			(values, entry) => ({ ...values, [addressDiffEntryToIdentifier(entry.currentContact)]: "updatedContact" }),
			{} as Record<string, ContactUpdateFormValue>,
		),
		companies: contactUpdateDiff.companies.reduce(
			(values, entry) => ({ ...values, [companyDiffEntryToIdentifier(entry.currentContact)]: "updatedContact" }),
			{} as Record<string, ContactUpdateFormValue>,
		),
	};
}
